import React, { useState, FC, useRef } from 'react';
import { useRouter } from 'next/router';
import { useToast } from '@common/toast';
import ConfirmModal from '@components/common/confirmModal';
import styles from '@components/acca/test/list/accaTestList.module.scss';
import { accApplyHistoryKeys, useDeleteApplyHistory } from '@repository/acc';
import { useAchievementLog } from '@repository/match/logging/useLogging';
import { ApplyHistoryVO } from '@domain/vo/acca/test/ApplyHistoryListVO';
import { AccaModalType } from '@domain/constant/acca/AccaModalType';
import useClickAway from '@utils/hooks/useClickOutside';
import { queryClient } from '@utils/queryClient';

import classnames from 'classnames/bind';
import UrlUtil from '@utils/urlUtil';
const cx = classnames.bind(styles);

interface JobdaContinueCardProps {
  applyUrl: string;
  userName: string;
  applyHistoryData: ApplyHistoryVO;
  setModalState: (type: AccaModalType) => void;
}

const JobdaContinueCard: FC<JobdaContinueCardProps> = ({ applyUrl, userName, applyHistoryData, setModalState }) => {
  const { query } = useRouter();
  const Toast = useToast();
  const cardRef = useRef<HTMLDivElement>(null);
  const [optionVisible, setOptionVisible] = useState(false);

  const { mutateAsync: saveAchievementLogAsync } = useAchievementLog();
  const { mutate: deleteApplyHistoryMutate } = useDeleteApplyHistory(
    async () => {
      Toast({
        iconType: 'info',
        content: '해당 응시가 리스트에서 제거되었습니다.',
      });
      await queryClient.refetchQueries(accApplyHistoryKeys, { active: true, exact: true });
    },
    (err) => {
      switch (err?.response?.data?.errorCode) {
        case 'B902':
          setModalState('DELETE_APPLY_CONTINUE_ERROR');
          return;
        default:
          Toast({ type: 'error', iconType: 'info', content: err?.response?.data?.message });
          return;
      }
    },
  );

  const { sn, code, createdDateTime } = applyHistoryData;

  //이어하기 버튼 클릭시
  const handleApplyTest = () => {
    ConfirmModal({
      size: 'xlarge',
      title: (
        <>
          이어하기를 하시면 응시 중단된 단계부터 시작합니다.
          <br />
          이어하시겠습니까?
        </>
      ),
      onConfirm: () => {
        if (!code) return null;

        UrlUtil.openWindowWithPost(applyUrl, {
          authenticationCode: code,
          name: userName,
        });

        query.source_page === 'MATCH' &&
          saveAchievementLogAsync({ achievementType: 'ACC_APPLY', sourcePage: query.source_page });
      },
    });
  };

  useClickAway(() => setOptionVisible(false), cardRef);

  const handleDeleteApplyHistory = () => {
    setOptionVisible(false);

    ConfirmModal({
      title: '정말 응시내역을 삭제하시겠습니까?',
      description: '응시 삭제시 해당 응시를 다시 할 수 없습니다.',
      onConfirm: () => {
        deleteApplyHistoryMutate([sn]);
      },
    });
  };

  return (
    <div className={cx('btnWrap')}>
      <a className={cx('btn')} role="button" onClick={handleApplyTest}>
        <div className={cx('textWrap')}>
          <span className={cx('badge', 'jobda')}>잡다</span>
          이어하기
          <span className={cx('time')}>{`${createdDateTime} 응시시작`}</span>
        </div>
      </a>

      <div className={cx('optionArea')} ref={cardRef}>
        <a className={cx('btnOption')} role="button" onClick={() => setOptionVisible(!optionVisible)}>
          <span className={cx('hidden')}>option</span>
        </a>
        {optionVisible && (
          <div className={cx('menuArea')}>
            <a className={cx('btnMenu')} role="button" onClick={handleDeleteApplyHistory}>
              삭제
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobdaContinueCard;
